/**
 * Meta (Facebook) Pixel.
 *
 * Lastes aldri av seg selv. Pikselet setter _fbp, og ekomloven paragraf 3-15
 * krever samtykke foer den informasjonskapselen skrives. Derfor hentes
 * fbevents.js foerst naar den besoekende har trykket «Godta alle» i
 * samtykkebanneret. Sier de nei, lastes ingenting fra Meta.
 *
 * Grunnkoden kjoerer kun i nettleseren, aldri under SSR.
 */

export const PIXEL_ID = "28551423451132454";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialised = false;

function injectBaseCode() {
  if (window.fbq) return;

  // Stub that queues calls until fbevents.js finishes loading, so events fired
  // immediately after init aren't lost.
  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue!.push(args);
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
}

/**
 * Laster pikselet og teller foerste sidevisning. Trygg aa kalle flere ganger.
 * Kalles kun etter at den besoekende har samtykket.
 */
export function initMetaPixel() {
  if (typeof window === "undefined" || initialised) return;
  initialised = true;

  injectBaseCode();
  window.fbq?.("init", PIXEL_ID);
  window.fbq?.("track", "PageView");
}

/**
 * PageView ved rutebytte. Pikselet teller bare selve dokumentlastingen selv,
 * saa en SPA maa sende resten. Gjoer ingenting foer samtykke er gitt.
 */
export function trackPageView() {
  if (typeof window === "undefined" || !initialised) return;
  window.fbq?.("track", "PageView");
}

export type LeadDetails = {
  /** Booked total in NOK. This is quoted value, not collected revenue. */
  value: number;
  /** e.g. "Komplett vask + Felgrens" */
  contentName?: string;
  /** Stable id so a future Conversions API call can be de-duplicated against this one. */
  eventId?: string;
};

/**
 * Sendes ved bekreftet bestilling. Tilsvarer standardhendelsen Lead i Events
 * Manager. Uten samtykke finnes ikke pikselet, og da sendes ingenting.
 */
export function trackLead({ value, contentName, eventId }: LeadDetails) {
  if (typeof window === "undefined" || !initialised) return;
  window.fbq?.(
    "track",
    "Lead",
    {
      value,
      currency: "NOK",
      ...(contentName ? { content_name: contentName } : {}),
    },
    ...(eventId ? [{ eventID: eventId }] : []),
  );
}
